import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "../api/apiClient";
import { useAuth } from "./AuthContext";
import { useSelection } from "../SelectionContext";
import SecureLogger from "../utils/secureLogger";
import { playBusinessAudioAlert } from "../utils/restaurantAudio";
import { PLANNING_NOTIFICATION_EVENT, subscribeToPlanningNotifications } from "../utils/planningNotificationStream";

const UNREAD_POLL_INTERVAL_MS = 60_000;

type UnreadCountUpdater = number | ((currentCount: number) => number);

interface RefreshUnreadCountOptions {
  suppressSound?: boolean;
  forceFresh?: boolean;
}

interface InFlightUnreadRequest {
  discardResponse: boolean;
  promise: Promise<number | undefined>;
  requestId: number;
  scopeKey: string;
  scopeGeneration: number;
  suppressSound: boolean;
}

interface QueuedUnreadRequest {
  promise: Promise<number | undefined>;
  scopeKey: string;
  scopeGeneration: number;
  suppressSound: boolean;
}

interface NotificationUnreadContextValue {
  unreadCount: number;
  refreshUnreadCount: (
    options?: RefreshUnreadCountOptions
  ) => Promise<number | undefined>;
  setUnreadCount: (updater: UnreadCountUpdater) => void;
  subscribe: () => () => void;
}

const NotificationUnreadContext =
  createContext<NotificationUnreadContextValue | null>(null);

const normalizeUnreadCount = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? Math.max(0, parsedValue) : 0;
};

const getInitialDocumentVisibility = () =>
  typeof document === "undefined" || document.visibilityState === "visible";

export const NotificationUnreadProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const authContext = useAuth() as { user: any; token?: string; hasPermission?: (key: string) => boolean } | null;
  const userId = authContext?.user?.id;
  const authToken = authContext?.token;
  const canReadPlanning = Boolean(authContext?.hasPermission?.("Ressources Humaines.Temps.Planning.view") && authContext?.hasPermission?.("Notifications.view"));
  const {
    selectedCompany: selectedCompanyId,
    selectedWarehouse: selectedWarehouseId,
  } = useSelection();
  const [unreadCount, setUnreadCountState] = useState(0);
  const [consumerCount, setConsumerCount] = useState(0);
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    getInitialDocumentVisibility
  );
  const hasUnreadBaselineRef = useRef(false);
  const previousUnreadCountRef = useRef(0);
  const inFlightRequestRef = useRef<InFlightUnreadRequest | null>(null);
  const queuedRequestRef = useRef<QueuedUnreadRequest | null>(null);
  const nextRequestIdRef = useRef(0);
  const latestStartedRequestIdRef = useRef(0);
  const hasConsumers = consumerCount > 0;

  const scopeKey = [
    userId || "none",
    selectedCompanyId || "none",
    selectedWarehouseId || "none",
  ].join(":");
  const activeScopeRef = useRef({ key: scopeKey, generation: 0 });
  if (activeScopeRef.current.key !== scopeKey) {
    activeScopeRef.current = {
      key: scopeKey,
      generation: activeScopeRef.current.generation + 1,
    };
  }

  const subscribe = useCallback(() => {
    setConsumerCount((currentCount) => currentCount + 1);

    return () => {
      setConsumerCount((currentCount) => Math.max(0, currentCount - 1));
    };
  }, []);

  const setUnreadCount = useCallback((updater: UnreadCountUpdater) => {
    setUnreadCountState((currentCount) => {
      const requestedCount =
        typeof updater === "function" ? updater(currentCount) : updater;
      const nextUnreadCount = normalizeUnreadCount(requestedCount);

      previousUnreadCountRef.current = nextUnreadCount;
      hasUnreadBaselineRef.current = true;

      return nextUnreadCount;
    });
  }, []);

  const startUnreadCountRequest = useCallback(
    (suppressSound = false) => {
      if (!userId || !selectedCompanyId) {
        return Promise.resolve(undefined);
      }

      const scopeGeneration = activeScopeRef.current.generation;
      const requestId = nextRequestIdRef.current + 1;
      nextRequestIdRef.current = requestId;
      latestStartedRequestIdRef.current = requestId;

      const request: InFlightUnreadRequest = {
        discardResponse: false,
        promise: Promise.resolve(undefined),
        requestId,
        scopeKey,
        scopeGeneration,
        suppressSound,
      };

      request.promise = (async () => {
        try {
          SecureLogger.debug("Calling unread count API");

          const response = await apiClient.get(
            "/api/notifications/unread-count",
            {
              params: {
                company_id: selectedCompanyId,
                user_id: userId,
                warehouse_id: selectedWarehouseId || undefined,
              },
              skipGlobalLoading: true,
            }
          );

          if (
            request.discardResponse ||
            activeScopeRef.current.key !== request.scopeKey ||
            activeScopeRef.current.generation !== request.scopeGeneration ||
            latestStartedRequestIdRef.current !== request.requestId
          ) {
            return undefined;
          }

          const nextUnreadCount = normalizeUnreadCount(
            response.data?.unread_count
          );

          if (
            !request.suppressSound &&
            hasUnreadBaselineRef.current &&
            nextUnreadCount > previousUnreadCountRef.current
          ) {
            void playBusinessAudioAlert("notification_unread");
          }

          previousUnreadCountRef.current = nextUnreadCount;
          hasUnreadBaselineRef.current = true;
          setUnreadCountState(nextUnreadCount);

          return nextUnreadCount;
        } catch (_error) {
          SecureLogger.error("Failed to fetch unread count");
          return undefined;
        } finally {
          if (inFlightRequestRef.current === request) {
            inFlightRequestRef.current = null;
          }
        }
      })();

      inFlightRequestRef.current = request;
      return request.promise;
    },
    [scopeKey, selectedCompanyId, selectedWarehouseId, userId]
  );

  const refreshUnreadCount = useCallback(
    (options: RefreshUnreadCountOptions = {}) => {
      if (!userId || !selectedCompanyId) {
        return Promise.resolve(undefined);
      }

      const currentScope = activeScopeRef.current;
      const existingRequest = inFlightRequestRef.current;
      if (
        !existingRequest ||
        existingRequest.scopeKey !== currentScope.key ||
        existingRequest.scopeGeneration !== currentScope.generation
      ) {
        return startUnreadCountRequest(Boolean(options.suppressSound));
      }

      if (options.suppressSound) {
        existingRequest.suppressSound = true;
      }

      if (!options.forceFresh) {
        return existingRequest.promise;
      }

      // Une lecture explicitement demandee apres une mutation doit partir
      // apres le polling courant et etre la seule a pouvoir publier son etat.
      existingRequest.discardResponse = true;

      const queuedRequest = queuedRequestRef.current;
      if (
        queuedRequest?.scopeKey === currentScope.key &&
        queuedRequest.scopeGeneration === currentScope.generation
      ) {
        queuedRequest.suppressSound =
          queuedRequest.suppressSound || Boolean(options.suppressSound);
        return queuedRequest.promise;
      }

      const nextRequest: QueuedUnreadRequest = {
        promise: Promise.resolve(undefined),
        scopeKey: currentScope.key,
        scopeGeneration: currentScope.generation,
        suppressSound: Boolean(options.suppressSound),
      };

      nextRequest.promise = existingRequest.promise.then(() => {
        if (queuedRequestRef.current === nextRequest) {
          queuedRequestRef.current = null;
        }

        if (
          activeScopeRef.current.key !== nextRequest.scopeKey ||
          activeScopeRef.current.generation !== nextRequest.scopeGeneration
        ) {
          return undefined;
        }

        return startUnreadCountRequest(nextRequest.suppressSound);
      });

      queuedRequestRef.current = nextRequest;
      return nextRequest.promise;
    },
    [selectedCompanyId, startUnreadCountRequest, userId]
  );

  useEffect(() => {
    hasUnreadBaselineRef.current = false;
    previousUnreadCountRef.current = 0;
    queuedRequestRef.current = null;
    setUnreadCountState(0);
  }, [scopeKey]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (
      !hasConsumers ||
      !isDocumentVisible ||
      !userId ||
      !selectedCompanyId
    ) {
      return undefined;
    }

    void refreshUnreadCount();
    const intervalId = window.setInterval(() => {
      if (
        typeof document === "undefined" ||
        document.visibilityState === "visible"
      ) {
        void refreshUnreadCount();
      }
    }, UNREAD_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    hasConsumers,
    isDocumentVisible,
    refreshUnreadCount,
    selectedCompanyId,
    userId,
  ]);

  useEffect(() => {
    if (!hasConsumers || !isDocumentVisible || !userId || !selectedCompanyId || !authToken || !canReadPlanning) return;
    return subscribeToPlanningNotifications({ token: authToken, companyId: selectedCompanyId, onChange: () => {
      void refreshUnreadCount({ forceFresh: true });
      window.dispatchEvent(new Event(PLANNING_NOTIFICATION_EVENT));
    } });
  }, [hasConsumers, isDocumentVisible, userId, selectedCompanyId, authToken, canReadPlanning, refreshUnreadCount]);

  const value = useMemo(
    () => ({
      unreadCount,
      refreshUnreadCount,
      setUnreadCount,
      subscribe,
    }),
    [refreshUnreadCount, setUnreadCount, subscribe, unreadCount]
  );

  return (
    <NotificationUnreadContext.Provider value={value}>
      {children}
    </NotificationUnreadContext.Provider>
  );
};

export const useNotificationUnread = () => {
  const context = useContext(NotificationUnreadContext);
  const subscribe = context?.subscribe;

  useEffect(() => {
    if (!subscribe) {
      return undefined;
    }

    return subscribe();
  }, [subscribe]);

  if (!context) {
    throw new Error(
      "useNotificationUnread must be used within a NotificationUnreadProvider"
    );
  }

  return {
    unreadCount: context.unreadCount,
    refreshUnreadCount: context.refreshUnreadCount,
    setUnreadCount: context.setUnreadCount,
  };
};
